import React, { useEffect, useMemo, useRef, useState } from "react";
import SpinnerField from "./SpinnerField.jsx";
import {
  areTimeValuesEqual,
  decrementTimeField,
  incrementTimeField,
  isCompleteTimeValue,
  normalizeTimeValue,
  sanitizeAmpmInput,
  sanitizeDigits,
} from "./spinnerUtils.js";
import { cn } from "./utils";

const DEFAULT_VALUE = {
  hour: "12",
  minute: "00",
  second: "00",
  ampm: "PM",
};

const LABELS_BY_SIZE = {
  default: {
    hour: "Hour",
    minute: "Minute",
    second: "Second",
    ampm: "AM/PM",
  },
  compact: {
    hour: "HR",
    minute: "MIN",
    second: "SEC",
    ampm: "AM/PM",
  },
};

const EMPTY_RELATED_TARGET = null;

function TimeSpinnerInput({
  value,
  onChange,
  onBlur,
  onKeyDown,
  invalidFields,
  disabledFields,
  size = "default",
  className,
}) {
  const [draft, setDraft] = useState(() => normalizeTimeValue(value, DEFAULT_VALUE));
  const lastValidRef = useRef(normalizeTimeValue(value, DEFAULT_VALUE));
  const rootRef = useRef(null);
  const pendingBlurValueRef = useRef(null);
  const labels = LABELS_BY_SIZE[size] ?? LABELS_BY_SIZE.default;
  const fieldRefs = useMemo(
    () => [React.createRef(), React.createRef(), React.createRef(), React.createRef()],
    [],
  );

  useEffect(() => {
    const nextDraft = value ? { ...DEFAULT_VALUE, ...value } : DEFAULT_VALUE;
    setDraft((currentDraft) =>
      areTimeValuesEqual(nextDraft, currentDraft) ? currentDraft : nextDraft,
    );

    if (isCompleteTimeValue(nextDraft)) {
      lastValidRef.current = normalizeTimeValue(nextDraft, lastValidRef.current);
    }
  }, [value]);

  const emitChange = (nextValue) => {
    setDraft(nextValue);
    pendingBlurValueRef.current = nextValue;
    onChange?.(nextValue);
    if (isCompleteTimeValue(nextValue)) {
      lastValidRef.current = normalizeTimeValue(nextValue, lastValidRef.current);
    }
  };

  const normalizeAndEmit = () => {
    const normalized = normalizeTimeValue(draft, lastValidRef.current);
    emitChange(normalized);
    return normalized;
  };

  const focusField = (index) => {
    fieldRefs[index]?.current?.focus();
  };

  const handleFieldChange = (field, rawValue) => {
    const nextValue =
      field === "ampm"
        ? { ...draft, ampm: sanitizeAmpmInput(rawValue) }
        : {
            ...draft,
            [field]: sanitizeDigits(
              rawValue,
              field === "hour" ? 2 : 2,
            ),
          };
    emitChange(nextValue);
  };

  const handleStep = (field, direction) => {
    const nextValue =
      direction === "increment"
        ? incrementTimeField(draft, field, lastValidRef.current)
        : decrementTimeField(draft, field, lastValidRef.current);
    emitChange(nextValue);
  };

  return (
    <div
      ref={rootRef}
      className={cn("grid min-w-0 items-end gap-1.5", className)}
      style={{
        gridTemplateColumns:
          "minmax(0,1fr) auto minmax(0,1fr) auto minmax(0,1fr) minmax(0,0.9fr)",
      }}
      onBlur={(event) => {
        const nextTarget = event.relatedTarget ?? EMPTY_RELATED_TARGET;
        if (rootRef.current?.contains(nextTarget)) return;

        const nextValue =
          pendingBlurValueRef.current ?? normalizeTimeValue(draft, lastValidRef.current);
        pendingBlurValueRef.current = null;
        onBlur?.(nextValue);
      }}
    >
      <SpinnerField
        ref={fieldRefs[0]}
        label={labels.hour}
        value={draft.hour}
        size={size}
        maxLength={2}
        invalid={Boolean(invalidFields?.hour)}
        disabled={Boolean(disabledFields?.hour)}
        onChange={(nextValue) => handleFieldChange("hour", nextValue)}
        onCommit={normalizeAndEmit}
        onIncrement={() => handleStep("hour", "increment")}
        onDecrement={() => handleStep("hour", "decrement")}
        onMoveNext={() => focusField(1)}
        onKeyDown={onKeyDown}
      />
      <span className="shrink-0 pb-2 text-sm font-semibold text-text-tertiary">:</span>
      <SpinnerField
        ref={fieldRefs[1]}
        label={labels.minute}
        value={draft.minute}
        size={size}
        maxLength={2}
        invalid={Boolean(invalidFields?.minute)}
        onChange={(nextValue) => handleFieldChange("minute", nextValue)}
        onCommit={normalizeAndEmit}
        onIncrement={() => handleStep("minute", "increment")}
        onDecrement={() => handleStep("minute", "decrement")}
        onMovePrevious={() => focusField(0)}
        onMoveNext={() => focusField(2)}
        onKeyDown={onKeyDown}
      />
      <span className="shrink-0 pb-2 text-sm font-semibold text-text-tertiary">:</span>
      <SpinnerField
        ref={fieldRefs[2]}
        label={labels.second}
        value={draft.second}
        size={size}
        maxLength={2}
        invalid={Boolean(invalidFields?.second)}
        onChange={(nextValue) => handleFieldChange("second", nextValue)}
        onCommit={normalizeAndEmit}
        onIncrement={() => handleStep("second", "increment")}
        onDecrement={() => handleStep("second", "decrement")}
        onMovePrevious={() => focusField(1)}
        onMoveNext={() => focusField(3)}
        onKeyDown={onKeyDown}
      />
      <SpinnerField
        ref={fieldRefs[3]}
        label={labels.ampm}
        value={draft.ampm}
        size={size}
        inputMode="text"
        maxLength={2}
        disabled={Boolean(disabledFields?.ampm)}
        onChange={(nextValue) => handleFieldChange("ampm", nextValue)}
        onCommit={normalizeAndEmit}
        onIncrement={() => handleStep("ampm", "increment")}
        onDecrement={() => handleStep("ampm", "decrement")}
        onMovePrevious={() => focusField(2)}
        onClick={() => {
          if (disabledFields?.ampm) return;
          handleStep("ampm", "increment");
        }}
        onKeyDown={onKeyDown}
      />
    </div>
  );
}

export default TimeSpinnerInput;
